import { RuleFilled } from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';

import { useVoiceboxConversation } from 'bones/components/molecules/Voicebox/context/VoiceboxConversation';
import { useVoiceboxPanelData } from 'bones/components/molecules/Voicebox/context/VoiceboxPanelData';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { useVoiceboxSettings } from 'bones/components/molecules/Voicebox/context/VoiceboxSettings';
import { VoiceboxPanelDataSources } from 'bones/components/molecules/Voicebox/panel/VoiceboxDataSources';
import { VoiceboxPanel } from 'bones/components/molecules/Voicebox/panel/VoiceboxPanel';
import { VoiceboxPanelAttributes } from 'bones/components/molecules/Voicebox/panel/VoiceboxPanelAttributes';
import { VoiceboxPanelBreadcrumb } from 'bones/components/molecules/Voicebox/panel/VoiceboxPanelBreadcrumb';
import { VoiceboxPanelInstances } from 'bones/components/molecules/Voicebox/panel/VoiceboxPanelInstances';
import { VoiceboxPanelRelationships } from 'bones/components/molecules/Voicebox/panel/VoiceboxPanelRelationships';
import { DEFAULT_MODEL_NAME } from 'bones/constants/model';
import { VoiceboxRequestMessage } from 'bones/constants/voicebox/message';
import { Button } from 'bones/design-system/components/Button';
import { ExplorerLogo } from 'bones/design-system/components/icons/ExplorerLogo';
import { Loading } from 'bones/design-system/components/Loading';
import { Tooltip } from 'bones/design-system/components/Tooltip';
import { generateExplorerLink } from 'bones/utils/generateExplorerLink';
import { usePortalDashboardUrl } from 'bones/utils/portal/usePortalDashboardUrl';
import { QueryBuilderModel } from 'bones/utils/queryBuilder/model';
import { getSettingsFromRequestMessage } from 'bones/utils/voicebox/getSettingsFromRequestMessage';

export interface VoiceboxIriPanelProps {
  iri: string;
  isDataSourcesOpen: boolean;
  messageId: string;
  setIsDataSourcesOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const VoiceboxIriPanel: React.VFC<VoiceboxIriPanelProps> = ({
  iri,
  isDataSourcesOpen,
  messageId,
  setIsDataSourcesOpen,
}) => {
  const { config, copy, testIds } = useVoiceboxSchema();
  const { conversationId, messages } = useVoiceboxConversation();
  const {
    addPanelHistory,
    dataByIri,
    getLabelOrLocalName,
    provenance: { graphsByType, loading, metadata },
  } = useVoiceboxPanelData();
  const { modelsByDatabaseId } = useVoiceboxSettings();

  const messageIdx = messages.findIndex(
    (message) => message.messageId === messageId
  );
  const prevRequestMessage = messages
    .slice(0, messageIdx)
    .reverse()
    .find((message): message is VoiceboxRequestMessage =>
      Boolean(message.isRequest)
    );

  const dashboardUrl = usePortalDashboardUrl(config.connectionIdx);

  const data = dataByIri[iri];
  if (!data || !prevRequestMessage) {
    return null;
  }

  const settings = getSettingsFromRequestMessage(
    prevRequestMessage,
    config.defaultSettings
  );

  const model = modelsByDatabaseId[settings.databaseId]?.[
    settings.model || DEFAULT_MODEL_NAME
  ] as QueryBuilderModel | undefined;

  if (
    model?.isLoadingClasses ||
    model?.isLoadingProperties ||
    !data.loaded.label
  ) {
    return (
      <div
        className={classNames(
          'vb-voicebox-panel',
          'vb-voicebox-panel--loading'
        )}
      >
        <Loading size="lg" />
      </div>
    );
  }

  const label = getLabelOrLocalName(iri);

  if (
    !data.loaded.body ||
    data.attributes.loading ||
    data.instances.loading ||
    data.relationships.loading
  ) {
    return (
      <VoiceboxPanel
        key={iri}
        breadcrumb={<VoiceboxPanelBreadcrumb />}
        title={label}
      >
        <div className="vb-voicebox-panel-body-loading">
          <Loading size="lg" />
        </div>
      </VoiceboxPanel>
    );
  }

  const dataSourceGraphs =
    !model || model?.classesByIri[iri]
      ? graphsByType[iri] || []
      : data.types.types
          .filter((typeIri) => model.classesByIri[typeIri])
          .map((typeIri) => graphsByType[typeIri] || [])
          .flat();

  return (
    <VoiceboxPanel
      key={iri}
      actions={
        <>
          {dashboardUrl ? (
            <Tooltip description={copy.panel.header.explorer}>
              <Button
                href={generateExplorerLink({
                  conversationId,
                  dashboardUrl,
                  databaseId: settings.databaseId,
                  model: settings.model,
                  namedGraphs: settings.namedGraphs,
                  reasoning: settings.reasoning,
                  iri,
                })}
                leftIcon={ExplorerLogo}
                leftIconProps={{ size: 'xl' }}
                rel="noreferrer"
                size="2xs"
                target="_blank"
                testId={testIds.panel.header.explorer}
                variant="ghost-gray"
              />
            </Tooltip>
          ) : null}
          {!model || !dataSourceGraphs.length ? null : (
            <Button
              active={isDataSourcesOpen}
              leftIcon={RuleFilled}
              onClick={() => setIsDataSourcesOpen((prev) => !prev)}
              size="sm"
              testId={testIds.panel.header.dataSources}
              variant="secondary2-gray"
            >
              {copy.panel.header.dataSources}
            </Button>
          )}
        </>
      }
      badges={
        !model || model.classesByIri[iri]
          ? undefined
          : data.types.types
              .filter((typeIri) => model.classesByIri[typeIri])
              .map((typeIri, typeIdx) => ({
                key: typeIri,
                children: getLabelOrLocalName(typeIri),
                colorId: model.classesByIri[typeIri].colorId,
                onClick: () => {
                  addPanelHistory({ iri: typeIri, messageId });
                },
                testId: testIds.panel.header.type(typeIri, typeIdx),
                variant: 'light',
              }))
      }
      breadcrumb={<VoiceboxPanelBreadcrumb />}
      title={label}
    >
      <>
        {!model || !dataSourceGraphs.length ? null : (
          <VoiceboxPanelDataSources
            graphs={dataSourceGraphs}
            isOpen={isDataSourcesOpen}
            loading={loading}
            messageId={messageId}
            metadata={metadata}
            model={model}
          />
        )}

        {/* TODO: Summary Description */}

        <VoiceboxPanelInstances
          instances={data.instances}
          messageId={messageId}
        />

        <VoiceboxPanelAttributes
          attributes={data.attributes.attributes}
          settings={settings}
        />

        <VoiceboxPanelRelationships
          messageId={messageId}
          relationships={data.relationships.relationships}
        />
      </>
    </VoiceboxPanel>
  );
};
